import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Server, Activity, Cpu, HardDrive, Wifi, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface ServiceMetric {
    name: string;
    status: 'healthy' | 'warning' | 'critical';
    cpu: number;
    memory: number;
    requests: number;
    uptime: string;
}

const INITIAL_SERVICES: ServiceMetric[] = [
    { name: 'API Server', status: 'healthy', cpu: 45, memory: 62, requests: 1250, uptime: '14d 6h' },
    { name: 'Database', status: 'healthy', cpu: 28, memory: 78, requests: 890, uptime: '14d 6h' },
    { name: 'Cache (Redis)', status: 'warning', cpu: 12, memory: 45, requests: 3200, uptime: '7d 2h' },
    { name: 'Queue Worker', status: 'healthy', cpu: 8, memory: 22, requests: 156, uptime: '14d 6h' },
];

export const ServerMonitoring = () => {
    const { completeModule } = useAcademyStore();
    const [services, setServices] = useState(INITIAL_SERVICES);
    const [alerts, setAlerts] = useState<string[]>([]);
    const [isSimulating, setIsSimulating] = useState(false);
    const [time, setTime] = useState(0);
    const [actionsPerformed, setActionsPerformed] = useState(0);
    const [completed, setCompleted] = useState(false);

    // Simulate real-time updates
    useEffect(() => {
        if (!isSimulating) return;

        const interval = setInterval(() => {
            setTime(t => t + 1);

            setServices(prev => prev.map(service => {
                // Randomly fluctuate metrics
                const cpuDelta = (Math.random() - 0.5) * 10;
                const memDelta = (Math.random() - 0.5) * 5;
                const reqDelta = Math.floor((Math.random() - 0.3) * 50);

                const newCpu = Math.max(5, Math.min(100, service.cpu + cpuDelta));
                const newMem = Math.max(10, Math.min(100, service.memory + memDelta));
                const newReq = Math.max(0, service.requests + reqDelta);

                let newStatus: 'healthy' | 'warning' | 'critical' = 'healthy';
                if (newCpu > 85 || newMem > 90) newStatus = 'critical';
                else if (newCpu > 70 || newMem > 80) newStatus = 'warning';

                // Generate alerts for status changes
                if (newStatus !== service.status && newStatus !== 'healthy') {
                    setAlerts(prev => [...prev.slice(-4), `⚠️ ${service.name}: Status changed to ${newStatus.toUpperCase()}`]);
                }

                return {
                    ...service,
                    cpu: Math.round(newCpu),
                    memory: Math.round(newMem),
                    requests: newReq,
                    status: newStatus,
                };
            }));
        }, 1000);

        return () => clearInterval(interval);
    }, [isSimulating]);

    // Check completion
    useEffect(() => {
        if (actionsPerformed >= 3 && !completed) {
            setCompleted(true);
            completeModule('server-monitoring', 100);
        }
    }, [actionsPerformed, completed, completeModule]);

    const restartService = (name: string) => {
        setServices(prev => prev.map(s =>
            s.name === name ? { ...s, status: 'healthy', cpu: 5, memory: 20 } : s
        ));
        setAlerts(prev => [...prev.slice(-4), `🔄 ${name} restarted successfully`]);
        setActionsPerformed(a => a + 1);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'healthy': return 'bg-green-500';
            case 'warning': return 'bg-yellow-500';
            case 'critical': return 'bg-red-500';
            default: return 'bg-slate-500';
        }
    };

    const getMeterColor = (value: number) => {
        if (value > 85) return 'bg-red-500';
        if (value > 70) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    return (
        <div className="min-h-screen bg-slate-900 font-sans text-white pt-24 pb-20">

            <div className="max-w-7xl mx-auto p-8">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-black font-comic flex items-center gap-3">
                            <Activity className="text-cyan-500" size={36} />
                            Mission Control
                        </h1>
                        <p className="text-slate-400 mt-1">Real-time server health monitoring</p>
                    </div>
                    <button
                        onClick={() => { setIsSimulating(!isSimulating); setActionsPerformed(a => a + 1); }}
                        className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 ${isSimulating ? 'bg-red-600 hover:bg-red-700' : 'bg-cyan-600 hover:bg-cyan-700'
                            }`}
                    >
                        {isSimulating ? <><RefreshCw className="animate-spin" size={18} /> Stop Simulation</> : <>Start Simulation</>}
                    </button>
                </div>

                {/* Service Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {services.map(service => (
                        <motion.div
                            key={service.name}
                            layout
                            className={`bg-slate-800 rounded-2xl p-6 border-2 ${service.status === 'critical' ? 'border-red-500 animate-pulse' :
                                service.status === 'warning' ? 'border-yellow-500' :
                                    'border-slate-700'
                                }`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-bold text-white">{service.name}</h3>
                                    <div className="text-xs text-slate-500 mt-1">Uptime: {service.uptime}</div>
                                </div>
                                <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)} ${service.status !== 'healthy' ? 'animate-pulse' : ''}`} />
                            </div>

                            {/* CPU */}
                            <div className="mb-3">
                                <div className="flex justify-between text-xs text-slate-400 mb-1">
                                    <span className="flex items-center gap-1"><Cpu size={12} /> CPU</span>
                                    <span>{service.cpu}%</span>
                                </div>
                                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <motion.div
                                        className={`h-full ${getMeterColor(service.cpu)}`}
                                        animate={{ width: `${service.cpu}%` }}
                                    />
                                </div>
                            </div>

                            {/* Memory */}
                            <div className="mb-3">
                                <div className="flex justify-between text-xs text-slate-400 mb-1">
                                    <span className="flex items-center gap-1"><HardDrive size={12} /> Memory</span>
                                    <span>{service.memory}%</span>
                                </div>
                                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <motion.div
                                        className={`h-full ${getMeterColor(service.memory)}`}
                                        animate={{ width: `${service.memory}%` }}
                                    />
                                </div>
                            </div>

                            {/* Requests */}
                            <div className="flex justify-between text-sm mb-4">
                                <span className="text-slate-400 flex items-center gap-1"><Wifi size={12} /> Req/min</span>
                                <span className="text-white font-bold">{service.requests}</span>
                            </div>

                            <button
                                onClick={() => restartService(service.name)}
                                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2"
                            >
                                <RefreshCw size={14} /> Restart
                            </button>
                        </motion.div>
                    ))}
                </div>

                {/* Alerts Log */}
                <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                    <h3 className="font-bold text-slate-300 mb-4 flex items-center gap-2">
                        <AlertTriangle className="text-yellow-500" size={18} /> Alert Log
                    </h3>
                    <div className="space-y-2 min-h-[100px]">
                        {alerts.length === 0 && (
                            <div className="text-slate-500 italic">No alerts. Start the simulation to see real-time monitoring...</div>
                        )}
                        <AnimatePresence>
                            {alerts.map((alert, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-sm font-mono p-2 bg-slate-900 rounded-lg text-slate-300"
                                >
                                    {alert}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>
                </div>

                <div className="mt-4 text-center text-slate-500 text-sm">
                    💡 Start simulation and restart a service to complete! Actions: {actionsPerformed}/3
                </div>
            </div>

            {completed && (
                <ModuleSuccess
                    title="Ops Commander"
                    nextModulePath="/"
                    onRetry={() => {
                        setCompleted(false);
                        setActionsPerformed(0);
                    }}
                >
                    <div className="bg-slate-700 p-4 rounded-xl text-left border border-slate-600">
                        <h4 className="font-bold text-cyan-400 mb-2">👁️ Eyes on the Pulse</h4>
                        <p className="text-slate-300 text-sm mb-4">
                            Monitoring is key to uptime! You spotted the issues and fixed them.
                        </p>
                        
                        <Link to="/games/regex-asteroids" className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-500 text-white font-bold flex items-center justify-center gap-2 transition-transform hover:scale-105 border-b-4 border-green-800">
                            ☄️ Play "Regex Asteroids" Arcade
                        </Link>
                        <p className="text-center text-xs text-green-400 mt-2 font-bold uppercase">Game Unlocked!</p>
                    </div>
                </ModuleSuccess>
            )}

            {/* Educational Content */}
            <div className="max-w-7xl mx-auto px-8 pb-12">
                <div className="bg-slate-800 p-8 rounded-3xl border border-slate-700">
                    <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
                        📉 The 4 Golden Signals
                    </h2>
                    <p className="text-slate-400 mb-6">
                        Google Site Reliability Engineers (SREs) watch 4 key metrics to know if a system is healthy.
                    </p>

                    <div className="grid md:grid-cols-4 gap-6">
                        <div className="bg-slate-900 p-5 rounded-xl border border-slate-600">
                            <h3 className="font-bold text-blue-400 mb-2">1. Latency</h3>
                            <p className="text-slate-400 text-xs">
                                "How long does it take?"<br />
                                (e.g., 200ms is fast, 2s is slow).
                            </p>
                        </div>
                        <div className="bg-slate-900 p-5 rounded-xl border border-slate-600">
                            <h3 className="font-bold text-green-400 mb-2">2. Traffic</h3>
                            <p className="text-slate-400 text-xs">
                                "How much demand is there?"<br />
                                (e.g., requests per second).
                            </p>
                        </div>
                        <div className="bg-slate-900 p-5 rounded-xl border border-slate-600">
                            <h3 className="font-bold text-red-400 mb-2">3. Errors</h3>
                            <p className="text-slate-400 text-xs">
                                "how often does it fail?"<br />
                                (e.g., 500 Server Error).
                            </p>
                        </div>
                        <div className="bg-slate-900 p-5 rounded-xl border border-slate-600">
                            <h3 className="font-bold text-yellow-400 mb-2">4. Saturation</h3>
                            <p className="text-slate-400 text-xs">
                                "How full is the tank?"<br />
                                (e.g., CPU utilization, Memory full).
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerMonitoring;
